// Čista logika stranice "Firme za dostavu" (bez Vue-a i bez mreže), da se može provjeriti u Node-u.
// Backend vraća firme sa ugniježdenim `children` (podružnice), `restaurant_delivery_company` (veze sa restoranima: restoran
// dobija samo svoj red, admin sve) i `delivery_company_user`. Ovdje se to spljošti u red koji ekran može odmah prikazati.

/** delivery_companies.active: 0 neaktivna, 1 aktivna, 2 obrisana (12.09.2026). GET ga vraća kao boolean, vidi N3 u docs. */
export const ACTIVE_OFF = 0;
export const ACTIVE_ON = 1;
export const ACTIVE_DELETED = 2;

/** delivery_companies.paying_type (firma -> Ordera): forma nudi i 4, koju backend odbija (N2 u docs, još bez odgovora) */
export const PLATFORM_PAYING_TYPES = [1, 2, 3, 4];
/** restaurant_delivery_company.paying_type (restoran -> firma) */
export const LINK_PAYING_TYPES = [1, 2, 3];

const text = (value) => (value === null || value === undefined ? "" : String(value));
const orNull = (value) => (value === "" || value === undefined ? null : value);
const toBool = (value) => value === true || value === 1 || value === "1" || value === "true";

export const parseAmount = (value) => {
  const normalized = text(value).trim().replace(/\s/g, "").replace(",", ".");
  if (normalized === "") {
    return null;
  }
  const number = Number(normalized);
  return Number.isFinite(number) ? number : NaN;
};

/** `settings` stiže kao JSON tekst, objekat ili null */
export const parseSettings = (settings) => {
  if (!settings) return {};
  if (typeof settings === "string") {
    try {
      return JSON.parse(settings) || {};
    } catch (error) {
      return {};
    }
  }
  return typeof settings === "object" ? settings : {};
};

/** null/undefined (stari redovi) se računa kao aktivna: model firme ima active: 1 kao početnu vrijednost */
export const normalizeActive = (value) => {
  if (value === null || value === undefined) return ACTIVE_ON;
  if (value === true || value === 1 || value === "1") return ACTIVE_ON;
  if (value === 2 || value === "2") return ACTIVE_DELETED;
  return ACTIVE_OFF;
};

/** red iz restaurant_delivery_company; ugniježđeni restoran se čita samo za naziv */
export const mapLink = (raw) => {
  const paying = parseAmount(raw.paying);
  const payType = raw.paying_type === null || raw.paying_type === undefined ? null : Number(raw.paying_type);
  return {
    id: raw.id,
    restaurantId: Number(raw.restaurant_id),
    restaurantName: text(raw.restaurant && raw.restaurant.name),
    activeRestaurant: toBool(raw.active_restoran),
    activeCompany: toBool(raw.active_company),
    payType: payType || null,
    paying: Number.isNaN(paying) ? null : paying,
    internal: toBool(raw.internal),
    raw,
  };
};

export const mapFirm = (raw, parent = null) => {
  const settings = parseSettings(raw.settings);
  const platformPaying = parseAmount(raw.paying);
  const usersCount = raw.active_users_count !== undefined && raw.active_users_count !== null ? raw.active_users_count : raw.users_count;
  const firm = {
    id: raw.id,
    name: text(raw.name),
    parentId: Number(raw.parent_id) || (parent ? parent.id : 0),
    parentName: parent ? parent.name : "",
    isChild: Boolean(parent),
    email: text(raw.email),
    phone: text(raw.phone),
    cityId: raw.city_id ? Number(raw.city_id) : 0,
    jib: text(raw.jib),
    pib: text(raw.pib),
    owner: text(raw.owner),
    contact: text(raw.contact_person),
    contract: text(raw.contract),
    note: text(raw.note),
    currency: text(settings.currency),
    active: normalizeActive(raw.active),
    showBreakdown: toBool(raw.show_price_breakdown),
    platformType: raw.paying_type === null || raw.paying_type === undefined ? null : Number(raw.paying_type) || null,
    platformAmount: Number.isNaN(platformPaying) ? null : platformPaying,
    ownerRestaurantId: raw.restaurant_id ? Number(raw.restaurant_id) : 0,
    usersCount: Number(usersCount) || 0,
    links: (raw.restaurant_delivery_company || []).map(mapLink),
    children: [],
    // zamrznuto: veliki ugniježđeni objekti (restorani sa korisnicima) ne ulaze u Vue praćenje
    raw: Object.freeze(raw),
  };
  firm.children = (raw.children || []).map((child) => mapFirm(child, firm));
  return firm;
};

/** Lista firmi: podružnice su ispod matične; red koji je i sam ugniježden kao podružnica ne ostaje i na vrhu. */
export const mapFirms = (list) => {
  const nested = new Set();
  (list || []).forEach((raw) => (raw.children || []).forEach((child) => nested.add(child.id)));
  return (list || []).filter((raw) => !nested.has(raw.id)).map((raw) => mapFirm(raw));
};

export const flattenFirms = (firms) => firms.reduce((all, firm) => all.concat(firm, firm.children), []);

// ------------------------------------------------------------------ pogled prijavljenog korisnika
/** Veza firme sa izabranim restoranom (restoran vidi samo svoj red, admin vidi sve, pa traži svoj po id-u). */
export const ownLink = (firm, restaurantId) => {
  if (!restaurantId) return null;
  return firm.links.find((link) => link.restaurantId === Number(restaurantId)) || null;
};

/** active | paused (restoran je isključio firmu) | inactive | deleted */
export const firmState = (firm, restaurantId) => {
  if (firm.active === ACTIVE_DELETED) return "deleted";
  if (firm.active === ACTIVE_OFF) return "inactive";
  const link = ownLink(firm, restaurantId);
  return link && !link.activeCompany ? "paused" : "active";
};

/** Aktivna veza bez dogovorene naplate: povratna informacija backenda 1.2, firma nastaje sa paying_type: null. */
export const billingMissing = (firm, restaurantId) => {
  const link = ownLink(firm, restaurantId);
  return Boolean(link && link.activeRestaurant && link.activeCompany && !link.payType && firm.active === ACTIVE_ON);
};

/**
 * Šta prijavljeni korisnik smije na redu (ista pravila kao stara stranica):
 * izmjena i saradnja samo admin i restoran, na vrhu samo kad firma ima restoran-vlasnika, a podružnice uvijek;
 * korisnici firme: restoran samo za svoju internu firmu, ostali osim kurira.
 */
export const capabilities = (firm, { userType, restaurantId }) => {
  const canManage = ["admin", "restaurant"].includes(userType);
  const owned = firm.isChild || Boolean(firm.ownerRestaurantId);
  let users;
  if (userType === "restaurant") {
    users = firm.links.some((link) => link.restaurantId === Number(restaurantId) && link.internal);
  } else {
    users = userType !== "dostava";
  }
  const link = ownLink(firm, restaurantId);
  return {
    edit: canManage && owned,
    cooperation: canManage && owned,
    users: users && owned,
    toggle: userType === "restaurant" && Boolean(link),
  };
};

// ------------------------------------------------------------------ brojke i filteri
const matches = (firm, status, restaurantId) => {
  if (status === "all") return true;
  if (status === "billing") return billingMissing(firm, restaurantId);
  const state = firmState(firm, restaurantId);
  return status === "active" ? state === "active" : state !== "active";
};

export const countFirms = (firms, restaurantId) => {
  const all = flattenFirms(firms);
  return {
    all: all.length,
    active: all.filter((firm) => firmState(firm, restaurantId) === "active").length,
    inactive: all.filter((firm) => firmState(firm, restaurantId) !== "active").length,
    billing: all.filter((firm) => billingMissing(firm, restaurantId)).length,
  };
};

/** tekst po kojem se firma pretražuje (grad dolazi spolja, jer firma nosi samo city_id) */
const haystack = (firm, cityName) => [firm.id, firm.name, firm.email, firm.phone, firm.jib, firm.pib, firm.owner, firm.contact, cityName(firm.cityId)].join(" ");

/**
 * Redovi tabele: matična firma pa njene podružnice. Matična koja ne zadovoljava filter ostaje kao "kontekst"
 * (prigušen red) ako je neka podružnica zadovoljava. Kad nema filtera, podružnice slijede stanje `open`.
 * status: all | active | inactive | billing
 */
export const buildRows = (firms, { status = "all", query = "", open = {}, restaurantId = 0 } = {}, toLatin = (s) => s, cityName = () => "") => {
  const needle = toLatin(text(query)).trim().toLowerCase();
  const filtering = status !== "all" || needle !== "";
  const hit = (firm) => matches(firm, status, restaurantId) && (!needle || toLatin(haystack(firm, cityName)).toLowerCase().includes(needle));
  const rows = [];
  firms.forEach((firm) => {
    const own = hit(firm);
    const kids = firm.children.filter(hit);
    if (!own && !kids.length) return;
    const expanded = filtering ? kids.length > 0 : open[firm.id] !== false;
    rows.push({ firm, depth: 0, context: !own, childCount: firm.children.length, expanded });
    if (expanded) {
      (filtering ? kids : firm.children).forEach((child) => rows.push({ firm: child, depth: 1, context: false, childCount: 0, expanded: false }));
    }
  });
  return rows;
};

// ------------------------------------------------------------------ forma firme
export const emptyFirmForm = () => ({
  id: 0,
  name: "",
  parentId: "",
  active: "1",
  countryId: "",
  cityId: "",
  phone: "",
  email: "",
  jib: "",
  pib: "",
  owner: "",
  contact: "",
  contract: "",
  note: "",
  currency: "",
  showBreakdown: false,
  platformType: "",
  platformAmount: "",
});

const amountToForm = (amount) => (amount === null || amount === undefined ? "" : String(amount).replace(".", ","));

/** `countryOfCity(cityId)` vraća id države čiji spisak gradova sadrži grad (država nije kolona firme, 12.09.2026 B2) */
export const formFromFirm = (firm, countryOfCity = () => "") => ({
  id: firm.id,
  name: firm.name,
  parentId: firm.parentId ? String(firm.parentId) : "",
  active: String(firm.active),
  countryId: firm.cityId ? String(countryOfCity(firm.cityId) || "") : "",
  cityId: firm.cityId ? String(firm.cityId) : "",
  phone: firm.phone,
  email: firm.email,
  jib: firm.jib,
  pib: firm.pib,
  owner: firm.owner,
  contact: firm.contact,
  contract: firm.contract,
  note: firm.note,
  currency: firm.currency,
  showBreakdown: firm.showBreakdown,
  platformType: firm.platformType ? String(firm.platformType) : "",
  platformAmount: amountToForm(firm.platformAmount),
});

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+$/;

/** Samo ono što backend sigurno traži (naziv i email); ostala pravila ostaju backendu. */
export const validateFirmForm = (form, { isAdmin = false } = {}) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "required";
  if (!form.email.trim()) errors.email = "required";
  else if (!EMAIL_SHAPE.test(form.email.trim())) errors.email = "email";
  if (isAdmin && form.platformAmount !== "" && Number.isNaN(parseAmount(form.platformAmount))) errors.platformAmount = "number";
  return errors;
};

/**
 * Tijelo POST/PUT .../companies-delivery: samo polja koja forma mijenja, nikad ugniježđeni objekti (BUG 15, 12.09.2026).
 * Država i adresa nisu kolone firme pa se ne šalju. paying_type/paying šalje samo admin: restoranu ih backend sakriva,
 * pa bi ih izmjena upisala kao null. `settings` zadržava ostale ključeve i mijenja samo valutu.
 */
export const buildFirmBody = (form, { isAdmin = false, original = null } = {}) => {
  const settings = { ...parseSettings(original && original.raw && original.raw.settings) };
  if (form.currency) settings.currency = form.currency;
  else delete settings.currency;
  const body = {
    name: form.name.trim(),
    email: form.email.trim(),
    parent_id: form.parentId ? Number(form.parentId) : 0,
    city_id: form.cityId ? Number(form.cityId) : null,
    phone: orNull(form.phone.trim()),
    jib: orNull(form.jib.trim()),
    pib: orNull(form.pib.trim()),
    owner: orNull(form.owner.trim()),
    contact_person: orNull(form.contact.trim()),
    contract: orNull(form.contract.trim()),
    settings: Object.keys(settings).length ? settings : null,
    note: orNull(form.note.trim()),
    active: Number(form.active),
    show_price_breakdown: Boolean(form.showBreakdown),
  };
  if (isAdmin) {
    body.paying_type = form.platformType ? Number(form.platformType) : null;
    const amount = parseAmount(form.platformAmount);
    body.paying = amount === null || Number.isNaN(amount) ? null : amount;
  }
  return body;
};

/** ključ u tijelu zahtjeva -> ključ u formi (da se 422 prikaže ispod pravog polja) */
const ERROR_FIELDS = {
  name: "name",
  email: "email",
  parent_id: "parentId",
  city_id: "cityId",
  phone: "phone",
  jib: "jib",
  pib: "pib",
  owner: "owner",
  contact_person: "contact",
  contract: "contract",
  note: "note",
  active: "active",
  settings: "currency",
  paying_type: "platformType",
  paying: "platformAmount",
};

/** 422 odgovor -> { fields: { formskiKljuč: poruka }, rest } (prva poruka po polju); `rest` je poruka bez polja. */
export const mapServerErrors = (response) => {
  const fields = {};
  let rest = "";
  const data = (response && response.data) || {};
  Object.keys(data.errors || {}).forEach((field) => {
    const message = [].concat(data.errors[field])[0];
    const key = ERROR_FIELDS[field.split(".")[0]];
    if (key) fields[key] = fields[key] || message;
    else if (!rest) rest = message;
  });
  if (!Object.keys(fields).length && !rest) {
    rest = text(data.message);
  }
  return { fields, rest };
};

// ------------------------------------------------------------------ veza restorana sa firmom
export const formFromLink = (link) => ({
  activeRestaurant: link ? link.activeRestaurant : true,
  activeCompany: link ? link.activeCompany : true,
  payType: link && link.payType ? String(link.payType) : "",
  paying: link ? amountToForm(link.paying) : "",
  internal: link ? link.internal : false,
});

export const validateLinkForm = (form) => {
  const errors = {};
  if (form.paying !== "" && Number.isNaN(parseAmount(form.paying))) errors.paying = "number";
  return errors;
};

/**
 * Tijelo POST /companies/companies-delivery-restaurant: jedan red (svoj restoran) i samo id firme (BUG 8 i 17, 12.09.2026).
 * "Nije izabrano" šalje 0 ako je firma ranije imala način plaćanja (tako se on poništava), inače null.
 */
export const buildLinkBody = (form, { firmId, restaurantId, original = null }) => {
  const amount = parseAmount(form.paying);
  let payType = null;
  if (form.payType) payType = Number(form.payType);
  else if (original && original.payType) payType = 0;
  return {
    restorani: [
      {
        id: restaurantId,
        restaurant_id: restaurantId,
        active_restoran: Boolean(form.activeRestaurant),
        active_company: Boolean(form.activeCompany),
        paying_type: payType,
        paying: amount === null || Number.isNaN(amount) ? null : amount,
        internal: Boolean(form.internal),
      },
    ],
    delivery_company: { id: firmId },
    restoran_id: restaurantId,
  };
};

/**
 * Tijelo POST /companies/change-company-delivery-status: red veze sa novim active_company i res_id.
 * Ugniježđeni objekti (restoran sa korisnicima) se ne šalju nazad, samo skalarna polja reda.
 */
export const buildStatusBody = (link, active) => {
  const body = {};
  Object.keys(link.raw).forEach((key) => {
    const value = link.raw[key];
    if (value === null || typeof value !== "object") body[key] = value;
  });
  body.active_company = active ? 1 : 0;
  body.res_id = link.raw.restaurant_id;
  return body;
};

// ------------------------------------------------------------------ prikaz
export const initials = (firm) => {
  const words = text(firm.name)
    .trim()
    .split(/\s+/)
    .filter((word) => /[\p{L}\p{N}]/u.test(word));
  if (!words.length) return "?";
  return (words.length === 1 ? words[0].slice(0, 2) : `${words[0].charAt(0)}${words[1].charAt(0)}`).toUpperCase();
};

/** "2,00 KM" ili "15,00 %" ili "" kad naplata nije unesena */
export const payingText = (type, amount, { currency = "KM", number = (n) => String(n) } = {}) => {
  if (!type || amount === null || amount === undefined) return "";
  return type === 2 ? `${number(amount)} %` : `${number(amount)} ${currency}`;
};
